import api from './api'

export class HorseImageService {
  private static cache = new Map<string, string>()

  /**
   * Busca a imagem do cavalo da API
   */
  static async fetchHorseImage(tenantId: number, horseId: number): Promise<string | null> {
    const cacheKey = `${tenantId}-${horseId}`
    
    // Verificar cache primeiro
    if (this.cache.has(cacheKey)) {
      console.log('🎯 Imagem encontrada no cache para:', cacheKey)
      return this.cache.get(cacheKey)!
    }

    try {
      console.log('📡 Buscando imagem do cavalo:', { tenantId, horseId })
      const response = await api.get(`/horse/${tenantId}/${horseId}`)
      
      console.log('📋 Resposta da API recebida')
      
      const base64Image = this.extractImageFromResponse(response.data)
      
      if (base64Image) {
        const processedImage = this.processBase64Image(base64Image)
        // Armazenar no cache
        this.cache.set(cacheKey, processedImage)
        console.log('✅ Imagem processada e armazenada no cache')
        return processedImage
      }
      
      console.log('⚠️ Campo horsePhoto não encontrado ou vazio na resposta')
      return null
    } catch (error) {
      console.error('❌ Erro ao buscar imagem do cavalo:', error)
      return null
    }
  }

  /**
   * Extrai a imagem base64 da resposta da API
   */
  private static extractImageFromResponse(data: any): string | null {
    if (!data) {
      console.log('❌ Dados da resposta estão vazios')
      return null
    }

    console.log('🔍 Procurando campo horsePhoto...')
    console.log('🔍 Chaves disponíveis:', Object.keys(data))

    // PRIMEIRO: Verificar se há um objeto 'horse' (estrutura aninhada comum)
    if (data.horse && typeof data.horse === 'object') {
      console.log('🎯 Encontrado objeto horse, procurando horsePhoto dentro dele...')
      console.log('🔍 Chaves em data.horse:', Object.keys(data.horse))
      
      if (data.horse.horsePhoto && typeof data.horse.horsePhoto === 'string') {
        console.log('✅ Campo horsePhoto encontrado em data.horse.horsePhoto!')
        console.log('📏 Tamanho da string base64:', data.horse.horsePhoto.length)
        console.log('🔤 Primeiros 50 caracteres:', data.horse.horsePhoto.substring(0, 50))
        return data.horse.horsePhoto
      }
    }

    // SEGUNDO: Verificar se há horsePhoto diretamente no objeto
    if (data.horsePhoto && typeof data.horsePhoto === 'string') {
      console.log('✅ Campo horsePhoto encontrado diretamente!')
      console.log('📏 Tamanho da string base64:', data.horsePhoto.length)
      console.log('🔤 Primeiros 50 caracteres:', data.horsePhoto.substring(0, 50))
      return data.horsePhoto
    }

    // TERCEIRO: Verificar outros objetos aninhados que possam conter horsePhoto
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'object' && value !== null) {
        const nestedValue = (value as any).horsePhoto
        if (nestedValue && typeof nestedValue === 'string') {
          console.log(`✅ Campo horsePhoto encontrado em: ${key}.horsePhoto`)
          console.log('📏 Tamanho da string base64:', nestedValue.length)
          return nestedValue
        }
      }
    }

    // Fallback: tentar outros campos possíveis
    const possibleFields = [
      'horseImage',
      'imageBase64', 
      'image',
      'foto',
      'picture',
      'base64Image'
    ]

    for (const field of possibleFields) {
      if (data[field] && typeof data[field] === 'string') {
        console.log(`🎯 Imagem encontrada no campo alternativo: ${field}`)
        return data[field]
      }
    }

    console.log('❌ Nenhum campo de imagem encontrado na resposta')
    return null
  }

  /**
   * Processa a string base64 para criar uma URL válida para img src
   */
  private static processBase64Image(base64: string): string {
    // Remove espaços em branco e quebras de linha
    const cleanBase64 = base64.trim().replace(/\s/g, '')
    
    // Verificar se já tem o prefixo data:
    if (cleanBase64.startsWith('data:')) {
      return cleanBase64
    }

    // Detectar o tipo de imagem baseado nos primeiros caracteres
    const imageType = this.detectImageType(cleanBase64)
    
    // Construir a URL completa
    return `data:${imageType};base64,${cleanBase64}`
  }

  /**
   * Detecta o tipo MIME da imagem baseado na assinatura base64
   */
  private static detectImageType(base64: string): string {
    // Primeiros caracteres indicam o tipo de arquivo
    if (base64.startsWith('/9j/')) return 'image/jpeg'
    if (base64.startsWith('iVBORw0KGgo')) return 'image/png'
    if (base64.startsWith('R0lGODlh') || base64.startsWith('R0lGODdh')) return 'image/gif'
    if (base64.startsWith('UklGR')) return 'image/webp'
    if (base64.startsWith('Qk0')) return 'image/bmp'
    
    // Default para JPEG se não conseguir detectar
    return 'image/jpeg'
  }

  /**
   * Limpa o cache de imagens
   */
  static clearCache(): void {
    this.cache.clear()
    console.log('🧹 Cache de imagens limpo')
  }

  /**
   * Remove uma imagem específica do cache
   */
  static removeCacheEntry(tenantId: number, horseId: number): void {
    const cacheKey = `${tenantId}-${horseId}`
    this.cache.delete(cacheKey)
    console.log('🗑️ Entrada removida do cache:', cacheKey)
  }
}